/*
 * InvisiBra: keyboard and screen-reader fixes for the header menu (main menu rewrite, 30 Sep 2026).
 * Loaded from sections/header.liquid. It builds on the theme's own markup and events: assets/main.js
 * (MainNavigation and the .mobile-nav-toggle handler) is left unedited.
 *
 * Desktop. In the inline header layout the visible top row is #proxy-nav, and its dropdown panels live in the
 * separate #main-nav further down the page, so by default Tab from a top item runs through the rest of the header
 * before it reaches the open panel. Here:
 *   - Enter, Space or ArrowDown on a top item with a panel opens it (through the theme's own mouseenter event) and
 *     moves focus to the panel's first link. Enter or Space again closes it.
 *   - Tab from an open top item goes into its panel; Tab past the last panel link closes it and moves to the next
 *     top item; Shift+Tab from the first link returns to the top item.
 *   - ArrowDown/ArrowUp move between the panel's links.
 *   - Escape closes the panel and returns focus to its top item.
 *   - aria-expanded stays in sync on the visible top row.
 * Mobile. The hamburger gets aria-expanded; the drawer is a labelled dialog (aria-modal while open); Escape goes back
 * from a sub-list, then closes the drawer (by clicking the theme's own buttons); the sub-list toggles get
 * aria-expanded.
 */
(() => {
  const OPEN = 'navigation__item--show-children';
  const CLOSED = 'ib-nav-closed'; // hides a panel that keeps focus within it (see invisibra-nav.css)

  function setUpDesktop(mainNav) {
    const proxyNav = document.getElementById(mainNav.dataset.proxyNav);
    const topItems = () => [...mainNav.querySelectorAll('.navigation__tier-1 > .navigation__item')];
    const proxyItems = () => (proxyNav ? [...proxyNav.querySelectorAll('.navigation__tier-1 > .navigation__item')] : []);
    const inline = () => Boolean(proxyNav && document.querySelector('.pageheader--layout-inline-permitted'));
    const topLink = (i) => {
      const li = (inline() ? proxyItems() : topItems())[i];
      return li ? li.querySelector(':scope > .navigation__link') : null;
    };
    const hasPanel = (i) => {
      const li = topItems()[i];
      return Boolean(li && li.querySelector(':scope > .navigation__tier-2-container'));
    };
    const isOpen = (i) => topItems()[i].classList.contains(OPEN);
    const visible = (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    const panelLinks = (i) => [...topItems()[i].querySelectorAll(':scope > .navigation__tier-2-container a[href]')].filter(visible);
    const keyboardOpened = new Set();

    // Which top item (by index) an element belongs to: a top link in either row, or anything in a panel.
    function indexOf(el) {
      if (proxyNav && proxyNav.contains(el)) {
        return proxyItems().indexOf(el.closest('.navigation__tier-1 > .navigation__item'));
      }
      if (mainNav.contains(el)) return topItems().findIndex((li) => li.contains(el));
      return -1;
    }

    function open(i) {
      const li = topItems()[i];
      li.classList.remove(CLOSED);
      if (!li.classList.contains(OPEN)) li.dispatchEvent(new Event('mouseenter'));
      keyboardOpened.add(i);
    }

    function close(i) {
      const li = topItems()[i];
      keyboardOpened.delete(i);
      li.dispatchEvent(new Event('mouseleave'));
      // In the row-under-the-logo layout the top link sits inside the li, and the theme's :focus-within rule
      // would keep the panel showing while focus is on it.
      li.classList.add(CLOSED);
    }

    // The theme opens a panel after a short delay; focus its first link as soon as it can take focus.
    function openAndFocus(i, which) {
      open(i);
      const start = performance.now();
      const attempt = () => {
        const links = panelLinks(i);
        if (links.length) {
          (which === 'last' ? links[links.length - 1] : links[0]).focus();
        } else if (performance.now() - start < 800) {
          requestAnimationFrame(attempt);
        }
      };
      requestAnimationFrame(attempt);
    }

    // After the last panel link: the next top item, or the account/search/basket links on the right.
    function focusAfter(i) {
      const next = topLink(i + 1);
      if (next) {
        next.focus();
        return;
      }
      const candidates = document.querySelectorAll('.logo-area__right shopify-account, .logo-area__right a[href], .logo-area__right button');
      for (const el of candidates) {
        el.focus();
        if (el === document.activeElement || el.contains(document.activeElement)) return;
      }
    }

    document.addEventListener('keydown', (evt) => {
      if (evt.altKey || evt.ctrlKey || evt.metaKey) return;
      const i = indexOf(evt.target);
      if (i < 0 || !hasPanel(i)) return;
      const onTop = evt.target === topLink(i);
      const handled = () => {
        evt.preventDefault();
        evt.stopPropagation(); // keep the theme's own Enter handler out of it
      };

      if (onTop) {
        if (evt.key === 'Enter' || evt.key === ' ') {
          handled();
          if (isOpen(i)) close(i);
          else openAndFocus(i, 'first');
        } else if (evt.key === 'ArrowDown') {
          handled();
          openAndFocus(i, 'first');
        } else if (evt.key === 'Escape' && isOpen(i)) {
          handled();
          close(i);
        } else if (evt.key === 'Tab' && !evt.shiftKey && isOpen(i) && inline()) {
          const links = panelLinks(i);
          if (links.length) {
            evt.preventDefault();
            links[0].focus();
          }
        }
        return;
      }

      // Focus is inside the panel.
      const links = panelLinks(i);
      const pos = links.indexOf(evt.target);
      if (evt.key === 'Escape') {
        handled();
        const top = topLink(i);
        close(i);
        if (top) top.focus();
      } else if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
        handled();
        if (evt.key === 'ArrowDown' && pos < links.length - 1) links[pos + 1].focus();
        if (evt.key === 'ArrowUp') {
          if (pos > 0) links[pos - 1].focus();
          else if (topLink(i)) topLink(i).focus();
        }
      } else if (evt.key === 'Tab' && inline()) {
        if (!evt.shiftKey && pos === links.length - 1) {
          evt.preventDefault();
          close(i);
          focusAfter(i);
        } else if (evt.shiftKey && pos === 0) {
          evt.preventDefault();
          topLink(i).focus();
        }
      }
    }, true);

    // Focus has moved somewhere else: close any panel the keyboard opened.
    document.addEventListener('focusin', (evt) => {
      topItems().forEach((li, i) => {
        const panel = li.querySelector(':scope > .navigation__tier-2-container');
        if (panel && panel.contains(evt.target)) {
          li.classList.remove(CLOSED);
          return;
        }
        if (evt.target === topLink(i) || li.contains(evt.target)) return; // back on its own top item
        if (keyboardOpened.has(i) && isOpen(i)) close(i);
        li.classList.remove(CLOSED);
      });
    });

    // A mouse (or the theme's touch handling) opening the panel again.
    topItems().forEach((li) => li.addEventListener('mouseenter', () => li.classList.remove(CLOSED)));

    // aria-expanded on both rows follows the theme's open class.
    const sync = () => {
      const proxies = proxyItems();
      topItems().forEach((li, i) => {
        const expanded = String(li.classList.contains(OPEN));
        [li, proxies[i]].forEach((item) => {
          const link = item && item.querySelector(':scope > .navigation__link[aria-haspopup]');
          if (link && link.getAttribute('aria-expanded') !== expanded) link.setAttribute('aria-expanded', expanded);
        });
      });
    };
    new MutationObserver(sync).observe(mainNav, { subtree: true, attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function setUpMobile() {
    const burger = document.querySelector('.logo-area .mobile-nav-toggle');
    const drawer = document.querySelector('.mobile-navigation-drawer');
    if (!burger || !drawer) return;
    const isDrawerOpen = () => document.body.classList.contains('reveal-mobile-nav');

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-label', burger.getAttribute('aria-label') || 'Menu');
    // The hamburger points at #main-nav, which is desktop-only; point it at the drawer it opens.
    if (!drawer.id) drawer.id = 'MobileNavigationDrawer';
    burger.setAttribute('aria-controls', drawer.id);

    const syncDrawer = () => {
      const openNow = String(isDrawerOpen());
      if (burger.getAttribute('aria-expanded') !== openNow) burger.setAttribute('aria-expanded', openNow);
      if (openNow === 'true') drawer.setAttribute('aria-modal', 'true');
      else drawer.removeAttribute('aria-modal');
    };
    new MutationObserver(syncDrawer).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    syncDrawer();

    // With "parent link opens the dropdown" on, the parent link does the toggle's job: make it the one control,
    // announced as a button, and take the duplicate chevron out of the tab order.
    const wholeLinkOpens = drawer.dataset.mobileExpandWithEntireLink === 'true';
    const parents = () => [...drawer.querySelectorAll('.navigation__item--with-children')];
    parents().forEach((li) => {
      const link = li.querySelector(':scope > .navigation__link');
      const toggle = li.querySelector(':scope > .navigation__children-toggle');
      if (wholeLinkOpens && link && toggle && li.parentElement.classList.contains('navigation__tier-1')) {
        link.setAttribute('role', 'button');
        toggle.setAttribute('tabindex', '-1');
        toggle.setAttribute('aria-hidden', 'true');
      }
    });

    const syncToggles = () => {
      parents().forEach((li) => {
        const expanded = String(li.classList.contains('navigation__item--open'));
        li.querySelectorAll(':scope > .navigation__link[aria-haspopup], :scope > .navigation__children-toggle').forEach((el) => {
          if (el.getAttribute('aria-expanded') !== expanded) el.setAttribute('aria-expanded', expanded);
        });
      });
    };
    new MutationObserver(syncToggles).observe(drawer, { subtree: true, attributes: true, attributeFilter: ['class'] });
    syncToggles();

    document.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape' || !isDrawerOpen()) return;
      evt.preventDefault();
      const back = drawer.querySelector('.mobile-nav-back');
      if (drawer.classList.contains('mobile-navigation-drawer--child-open') && back) back.click();
      else {
        const closeButton = drawer.querySelector('.mobile-nav-toggle');
        if (closeButton) closeButton.click();
      }
    });
  }

  const start = () => {
    const mainNav = document.getElementById('main-nav');
    if (!mainNav) return;
    setUpDesktop(mainNav);
    setUpMobile();
  };

  // MainNavigation (assets/main.js) builds the mobile drawer when it connects.
  const ready = window.customElements ? window.customElements.whenDefined('main-navigation') : Promise.resolve();
  ready.then(() => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  });
})();
